import React, { useState } from 'react';
import { View, Pressable, StyleSheet } from 'react-native';
import { Text } from '@constants/Themed';
import { Entypo, AntDesign } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import AnimatedLottieView from '@utils/lottieAnimation';
import type { MatchedAction } from '@utils/semanticActionMapper';
import type { ActionTemplate } from '@utils/semanticActionMapper';
import SafeMarkDown from './SafeMarkDown';
import { formatActionDescriptionForEasyMode } from '@utils/easyModeActionFormatter';

interface ActionCardMessageProps {
  currentMessage: any;
  theme: boolean;
  handleActionCardClick: (template: ActionTemplate, extractedData?: any) => void;
  styles: any; // Pass styles from parent
  showEditor?: boolean;
  isEditorConnected?: boolean;
  handleSendToEditor?: (text: string, format?: 'plain' | 'html') => void;
  handleSendSummaryToEditor?: () => void;
  handleSendConversationToEditor?: () => void;
}

const ActionCardMessage: React.FC<ActionCardMessageProps> = React.memo(({
  currentMessage,
  theme,
  handleActionCardClick,
  styles,
  showEditor,
  isEditorConnected,
  handleSendToEditor,
  handleSendSummaryToEditor,
  handleSendConversationToEditor
}) => {
  const [showInstructions, setShowInstructions] = useState(false);
  const hasText = currentMessage.text && currentMessage.text.trim().length > 0;

  return (
    <Animatable.View
      useNativeDriver
      style={[styles.systemMessageContainer, { marginBottom: 20 }]}
      animation={'fadeInUp'}
    >
      <AnimatedLottieView
        source={require("@assets/animations/automation_robot.json")}
        autoPlay
        loop
        style={{ width: 28, height: 28, marginRight: 12, alignSelf: "flex-start", marginTop: 10 }}
      />
      <View style={[styles.systemMessageBubble, { backgroundColor: theme ? '#f8f9fa' : '#2a2a2a', width: '90%', maxWidth: '90%', padding: 12 }]}>
        {/* Action Cards Section - Prominent */}
        <View style={{ marginBottom: hasText ? 12 : 0 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
            <Entypo name="rocket" size={16} color={theme ? '#3b82f6' : '#60a5fa'} style={{ marginRight: 8 }} />
            <Text style={{ fontWeight: '600', fontSize: 14, color: theme ? '#374151' : '#e5e7eb' }}>
              Suggested Actions
            </Text>
          </View>
          <View style={{ gap: 8 }}>
            {currentMessage.actionCards.map((matchedAction: MatchedAction & { extractedData?: any }, index: number) => {
              const { intent, confidence, template, extractedData } = matchedAction;
              const getCategoryColor = (category: string, destructive: boolean) => {
                if (destructive) return '#ef4444';
                switch (category) {
                  case 'navigation': return '#3b82f6';
                  case 'data': return '#10b981';
                  case 'ui': return '#8b5cf6';
                  case 'modal': return '#f59e0b';
                  case 'image': return '#f59e0b';
                  case 'automation': return '#f59e0b';
                  case 'document': return '#f59e0b';
                  default: return '#6b7280';
                }
              };
              const categoryColor = getCategoryColor(template.category, template.destructive);

              return (
                <Pressable
                  key={intent}
                  onPress={() => handleActionCardClick(template, extractedData)}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    backgroundColor: theme ? 'white' : '#1e293b',
                    borderRadius: 12,
                    padding: 12,
                    borderWidth: 1.5,
                    borderColor: categoryColor,
                    shadowColor: categoryColor,
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.2,
                    shadowRadius: 4,
                    elevation: 2,
                  }}
                >
                  {template.requiresData && (
                    <AntDesign name="form" size={16} color={categoryColor} style={{ marginRight: 8 }} />
                  )}
                  <View style={{ flex: 1 }}>
                    <Text style={{
                      fontSize: 13,
                      fontWeight: '600',
                      color: theme ? '#1f2937' : '#f3f4f6',
                      marginBottom: 4
                    }}>
                      {formatActionDescriptionForEasyMode(template.description || template.intent, template.intent)}
                    </Text>
                    <Text style={{
                      fontSize: 10,
                      color: theme ? '#6b7280' : '#9ca3af'
                    }}>
                      {(confidence * 100).toFixed(0)}% match
                    </Text>
                  </View>
                  <View style={{
                    backgroundColor: categoryColor,
                    borderRadius: 20,
                    width: 32,
                    height: 32,
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginLeft: 8
                  }}>
                    <AntDesign name="caret-right" size={16} color="white" />
                  </View>
                </Pressable>
              );
            })}
          </View>
        </View>

        {/* Collapsible Instructions Section */}
        {hasText && (
          <View style={{ marginTop: 12, borderTopWidth: 1, borderTopColor: theme ? '#e5e7eb' : '#374151', paddingTop: 12 }}>
            <Pressable
              onPress={() => setShowInstructions(!showInstructions)}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingVertical: 8,
                paddingHorizontal: 12,
                backgroundColor: theme ? '#ffffff' : '#1e293b',
                borderRadius: 8,
                marginBottom: showInstructions ? 12 : 0
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <AntDesign
                  name={showInstructions ? "down" : "right"}
                  size={14}
                  color={theme ? '#6b7280' : '#9ca3af'}
                  style={{ marginRight: 8 }}
                />
                <Text style={{
                  fontSize: 13,
                  fontWeight: '500',
                  color: theme ? '#6b7280' : '#9ca3af'
                }}>
                  View Instructions
                </Text>
              </View>
              <AntDesign
                name={showInstructions ? "up" : "down"}
                size={12}
                color={theme ? '#6b7280' : '#9ca3af'}
              />
            </Pressable>
            {showInstructions && (
              <View style={{
                backgroundColor: theme ? '#ffffff' : '#1e293b',
                borderRadius: 8,
                padding: 12,
                marginTop: 8
              }}>
                <SafeMarkDown
                  content={typeof currentMessage.text === 'string'
                    ? currentMessage.text
                    : (currentMessage.text ? JSON.stringify(currentMessage.text) : '')
                  }
                />
              </View>
            )}
          </View>
        )}
        
        {/* Editor Action Buttons - Show when editor is available */}
        {(showEditor || isEditorConnected) && currentMessage.text && typeof currentMessage.text === 'string' && currentMessage.text.trim().length > 0 && handleSendToEditor && (
          <View style={{
            flexDirection: 'row',
            gap: 8,
            marginTop: 12,
            paddingTop: 12,
            borderTopWidth: 1,
            borderTopColor: theme ? '#e5e7eb' : '#374151'
          }}>
            <Pressable
              onPress={() => handleSendToEditor(currentMessage.text, 'html')}
              style={{
                flex: 1,
                backgroundColor: theme ? '#7c3aed' : '#6d28d9',
                paddingVertical: 8,
                paddingHorizontal: 12,
                borderRadius: 6,
                alignItems: 'center',
                flexDirection: 'row',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <AntDesign name="file-text" size={14} color="white" />
              <Text style={{ color: 'white', fontSize: 12, fontWeight: '600' }}>
                Send to Editor
              </Text>
            </Pressable>
            {handleSendSummaryToEditor && (
              <Pressable
                onPress={handleSendSummaryToEditor}
                style={{
                  flex: 1,
                  backgroundColor: theme ? '#10b981' : '#059669',
                  paddingVertical: 8,
                  paddingHorizontal: 12,
                  borderRadius: 6,
                  alignItems: 'center',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <AntDesign name="file-text" size={14} color="white" />
                <Text style={{ color: 'white', fontSize: 12, fontWeight: '600' }}>
                  Summary
                </Text>
              </Pressable>
            )}
            {handleSendConversationToEditor && (
              <Pressable
                onPress={handleSendConversationToEditor}
                style={{
                  flex: 1,
                  backgroundColor: theme ? '#3b82f6' : '#2563eb',
                  paddingVertical: 8,
                  paddingHorizontal: 12,
                  borderRadius: 6,
                  alignItems: 'center',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <AntDesign name="message" size={14} color="white" />
                <Text style={{ color: 'white', fontSize: 12, fontWeight: '600' }}>
                  All Messages
                </Text>
              </Pressable>
            )}
          </View>
        )}
        
        {/* Editor Action Buttons - Show when editor is available */}
        {(showEditor || isEditorConnected) && currentMessage.text && typeof currentMessage.text === 'string' && currentMessage.text.trim().length > 0 && handleSendToEditor && (
          <View style={{
            flexDirection: 'row',
            gap: 8,
            marginTop: 12,
            paddingTop: 12,
            borderTopWidth: 1,
            borderTopColor: theme ? '#e5e7eb' : '#374151'
          }}>
            <Pressable
              onPress={() => handleSendToEditor(currentMessage.text, 'html')}
              style={{
                flex: 1,
                backgroundColor: theme ? '#7c3aed' : '#6d28d9',
                paddingVertical: 8,
                paddingHorizontal: 12,
                borderRadius: 6,
                alignItems: 'center',
                flexDirection: 'row',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <AntDesign name="file-text" size={14} color="white" />
              <Text style={{ color: 'white', fontSize: 12, fontWeight: '600' }}>
                Send to Editor
              </Text>
            </Pressable>
            {handleSendSummaryToEditor && (
              <Pressable
                onPress={handleSendSummaryToEditor}
                style={{
                  flex: 1,
                  backgroundColor: theme ? '#10b981' : '#059669',
                  paddingVertical: 8,
                  paddingHorizontal: 12,
                  borderRadius: 6,
                  alignItems: 'center',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <AntDesign name="file-text" size={14} color="white" />
                <Text style={{ color: 'white', fontSize: 12, fontWeight: '600' }}>
                  Summary
                </Text>
              </Pressable>
            )}
            {handleSendConversationToEditor && (
              <Pressable
                onPress={handleSendConversationToEditor}
                style={{
                  flex: 1,
                  backgroundColor: theme ? '#3b82f6' : '#2563eb',
                  paddingVertical: 8,
                  paddingHorizontal: 12,
                  borderRadius: 6,
                  alignItems: 'center',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <AntDesign name="message" size={14} color="white" />
                <Text style={{ color: 'white', fontSize: 12, fontWeight: '600' }}>
                  All Messages
                </Text>
              </Pressable>
            )}
          </View>
        )}
      </View>
    </Animatable.View>
  );
});

ActionCardMessage.displayName = 'ActionCardMessage';

export default ActionCardMessage;

